/**
 * Acknowledgment, on the document it is about — §7.7.
 *
 * This is the card the document screen mounts, and it is where the act belongs:
 * the row means "I read this version", so the button that writes it sits under
 * the text it attests to rather than on a list of titles somewhere else.
 *
 * Superseded cycles are shown beside the current one rather than hidden, because
 * §7.7's whole argument for version scoping is that a rewrite must not inherit
 * sign-off — and the evidence of that is the previous cycle sitting there with
 * its own numbers. "41 of 41 on version 3, 6 of 41 on version 4" is the sentence
 * the design exists to make sayable, and it is only sayable if both rows are on
 * screen.
 *
 * `progressVisible` decides whether the numbers arrive at all. When they do not,
 * {@link CycleProgressView} says so in words; nothing here draws an empty bar in
 * their place.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useDocumentAcknowledgments } from '../../api/queries.ts';
import type { AcknowledgmentCycle, DocumentStatus } from '../../api/types.ts';
import { formatDateTime } from '../../components/format.ts';
import { Button } from '../../components/ui/button.tsx';
import { ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { Card, CardHeader } from '../../components/ui/layout.tsx';
import { AcknowledgeAction } from './acknowledge.tsx';
import { OpenAcknowledgmentDialog } from './open.tsx';
import { CycleProgressView } from './progress.tsx';
import { AudienceDescription, CycleScopeBadge, DueDate } from './vocabulary.tsx';

/**
 * §7.7 — "valid only on published documents".
 *
 * `needs_review` counts, for the reason the API gives: §7.2 keeps a lapsed
 * document fully readable, so refusing here would make a policy
 * unacknowledgeable at exactly the moment it is overdue for a re-read.
 */
function isAcknowledgeable(status: DocumentStatus): boolean {
  return status === 'published' || status === 'needs_review';
}

function CycleRow({ cycle }: { cycle: AcknowledgmentCycle }) {
  return (
    <li className="flex flex-col gap-2 border-t border-secondary px-5 py-4 first:border-t-0">
      <div className="flex flex-wrap items-center gap-2">
        <CycleScopeBadge cycle={cycle} />
        <span className="text-xs text-tertiary">opened {formatDateTime(cycle.createdAt)}</span>
        <Link
          to="/acknowledgments/$requestId"
          params={{ requestId: cycle.requestId }}
          className="ml-auto text-xs link"
        >
          {cycle.progress ? 'Who owes it' : 'The cycle'}
        </Link>
      </div>

      <p className="text-sm text-secondary">
        <AudienceDescription audience={cycle.audience} />
      </p>
      <p className="text-sm text-tertiary">
        <DueDate dueDate={cycle.dueDate} />
      </p>

      <CycleProgressView progress={cycle.progress} compact />

      {/* The action, for the person who has just read the thing — and, on the
          rows where there is nothing to press, where they stand. Rendered
          unconditionally: `AcknowledgeAction` already decides between the four
          states, and gating it on `current` here re-decided one of them and lost
          the rest, so a superseded row said nothing about whether its reader had
          signed it. The badge above says which cycle this is; this says what it
          asks of the person reading. */}
      <AcknowledgeAction cycle={cycle} />
    </li>
  );
}

export interface AcknowledgmentCardProps {
  nodeId: string;
  title: string;
  documentStatus: DocumentStatus;
  /**
   * Whether to offer the request button. Opening a cycle is `document.publish`
   * on the node, resolved in SQL inside the mutating transaction; this only
   * decides whether a button that would 403 is worth drawing.
   */
  canOpen?: boolean;
}

export function AcknowledgmentCard({
  nodeId,
  title,
  documentStatus,
  canOpen = false,
}: AcknowledgmentCardProps) {
  const cycles = useDocumentAcknowledgments(nodeId);
  const [asking, setAsking] = useState(false);

  const rows = cycles.data?.cycles ?? [];
  const acknowledgeable = isAcknowledgeable(documentStatus);

  return (
    <>
      <Card padded={false}>
        <CardHeader
          title="Acknowledgment"
          description="Scoped to a version (§7.7). Publishing a revision opens a new cycle; these signatures do not carry into it."
          actions={
            canOpen && acknowledgeable ? (
              <Button variant="secondary" onPress={() => setAsking(true)}>
                Ask for acknowledgment
              </Button>
            ) : null
          }
        />

        {cycles.isPending ? (
          <div className="px-5">
            <Loading label="Resolving audiences…" />
          </div>
        ) : null}
        {cycles.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={cycles.error} />
          </div>
        ) : null}

        {!cycles.isPending && rows.length === 0 ? (
          <div className="px-5 py-4 text-sm text-tertiary">
            {acknowledgeable
              ? 'Nobody has been asked to acknowledge this. A cycle names an audience by rule — a role, or a capability held on a space — and everybody it resolves to owes a read of the published version.'
              : 'A cycle can only be opened on a published document (§7.7), so there is nothing to acknowledge yet.'}
          </div>
        ) : null}

        {rows.length > 0 ? (
          <ul className="flex flex-col">
            {rows.map((cycle) => (
              <CycleRow key={cycle.requestId} cycle={cycle} />
            ))}
          </ul>
        ) : null}
      </Card>

      <OpenAcknowledgmentDialog
        nodeId={nodeId}
        title={title}
        isOpen={asking}
        onOpenChange={setAsking}
      />
    </>
  );
}
